import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { FormField } from '~/components/FormField';
import { FormInputConfig } from '~/types/residentManagement';
import Spacer from '~/components/Spacer/Spacer';
import {
  categoryField,
  cityField,
  descriptionField,
  mapsLinkField,
  nameField,
  streetField,
  zipField
} from './constants';
import { IAddPlaceForm, PlaceLocationMode } from './interfaces';
import classes from './Places.module.scss';

type IProps = {
  onSubmit: (form: IAddPlaceForm) => void;
  cancel: () => void;
  submitting?: boolean;
};

const locationModeOptions: { mode: PlaceLocationMode; label: string }[] = [
  { mode: 'mapsLink', label: 'Google Maps link' },
  { mode: 'address', label: 'Address' }
];

const AddPlaceForm = ({ onSubmit, cancel, submitting }: IProps) => {
  const { register, handleSubmit, errors, control } = useForm<IAddPlaceForm>({
    defaultValues: { locationMode: 'mapsLink' }
  });
  const [locationMode, setLocationMode] =
    useState<PlaceLocationMode>('mapsLink');

  const submit: SubmitHandler<IAddPlaceForm> = (form) => {
    // Only the active mode's location fields are meaningful — the maps link
    // takes priority whenever it's present, so drop the other mode's fields
    // rather than submitting stale/empty values alongside it.
    const location =
      locationMode === 'mapsLink'
        ? { mapsLink: form.mapsLink }
        : { street: form.street, city: form.city, zip: form.zip };

    onSubmit({
      name: form.name,
      category: form.category,
      description: form.description,
      locationMode,
      ...location
    });
  };

  const renderField = (field: FormInputConfig) => (
    <div className={classes.field} key={field.id}>
      <p className={classes.fieldLabel}>
        {field.label}
        {!field.isRequired && (
          <span
            className='margin-left-8'
            style={{ color: 'var(--cobuLightGreyPointFive)', fontWeight: 400 }}
          >
            (optional)
          </span>
        )}
      </p>
      <FormField
        fieldConfig={field}
        register={register}
        control={control}
        errors={errors}
      />
    </div>
  );

  return (
    <form onSubmit={handleSubmit(submit)}>
      {renderField(nameField)}

      <p className={classes.fieldLabel}>Location</p>
      <div className={classes.locationModeToggle}>
        {locationModeOptions.map(({ mode, label }) => (
          <button
            type='button'
            key={mode}
            className={`${classes.locationModeOption} ${
              locationMode === mode ? classes.locationModeOptionActive : ''
            }`}
            onClick={() => setLocationMode(mode)}
          >
            {label}
          </button>
        ))}
      </div>

      {locationMode === 'mapsLink' ? (
        renderField(mapsLinkField)
      ) : (
        <>
          {renderField(streetField)}
          <div className={classes.fieldRow}>
            {renderField(cityField)}
            {renderField(zipField)}
          </div>
        </>
      )}

      {renderField(categoryField)}
      {renderField(descriptionField)}
      <Spacer height={16} />
      <div className={classes.formButtons}>
        <button
          type='button'
          className='btn-standard btn-secondary'
          onClick={cancel}
        >
          Cancel
        </button>
        <button
          type='submit'
          className='btn-standard btn-primary'
          disabled={submitting}
        >
          Add place
        </button>
      </div>
    </form>
  );
};

export default AddPlaceForm;
